import axios, { AxiosResponse } from 'axios';
import { AuthResponse, LoginRequest, RegisterRequest, User, BusinessCard } from '../types';

const API_BASE_URL = '/api/bizcard/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bizcard_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('bizcard_refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('bizcard_access_token', access_token);

          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('bizcard_access_token');
          localStorage.removeItem('bizcard_refresh_token');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
    return response.data;
  },

  async register(userData: RegisterRequest | FormData): Promise<AuthResponse> {
    if (userData instanceof FormData) {
      const response: AxiosResponse<AuthResponse> = await api.post('/auth/register-with-file', userData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response: AxiosResponse<AuthResponse> = await api.post('/auth/register', userData);
      return response.data;
    }
  },

  async getUsersForLogin(): Promise<User[]> {
    const response: AxiosResponse<User[]> = await axios.get(`${API_BASE_URL}/auth/users`);
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await api.get('/auth/me');
    return response.data;
  },

  async updateUser(userId: string, userData: Partial<RegisterRequest> | FormData): Promise<User> {
    if (userData instanceof FormData) {
      const response: AxiosResponse<User> = await api.put(`/auth/users/${userId}`, userData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // 通常のJSON更新（従来通り）
      const response: AxiosResponse<User> = await api.put(`/auth/users/${userId}`, userData);
      return response.data;
    }
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/auth/users/${userId}`);
  },

  logout() {
    localStorage.removeItem('bizcard_access_token');
    localStorage.removeItem('bizcard_refresh_token');
  },
};

export const businessCardService = {
  async getAll(): Promise<BusinessCard[]> {
    const response: AxiosResponse<BusinessCard[]> = await api.get('/cards/');
    return response.data;
  },

  async getWithFilters(params: { keyword?: string; tags?: string }): Promise<BusinessCard[]> {
    const queryParams = new URLSearchParams();
    if (params.keyword) queryParams.append('keyword', params.keyword);
    if (params.tags) queryParams.append('tags', params.tags);

    const url = `/cards/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: AxiosResponse<BusinessCard[]> = await api.get(url);
    return response.data;
  },

  async getTags(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await api.get('/tags/');
    return response.data;
  },

  async create(cardData: any): Promise<BusinessCard> {
    try {
      // FormDataかJSONかを判定
      if (cardData instanceof FormData) {
        const response: AxiosResponse<BusinessCard> = await api.post('/cards/', cardData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } else {
        // JSONデータをFormDataに変換
        const formData = new FormData();
        Object.entries(cardData).forEach(([key, value]) => {
          if (key === 'tag_names' && Array.isArray(value) && value.length > 0) {
            formData.append('tag_names', value.join(','));
          } else if (key === 'full_name' || key === 'full_name_reading') {
            // full_name と full_name_reading は空でも送信
            formData.append(key, String(value || ''));
          } else if (value !== null && value !== undefined && value !== '' && key !== 'tag_names') {
            formData.append(key, String(value));
          }
        });

        console.log('FormData entries for creation:');
        formData.forEach((value, key) => {
          console.log(key + ': ' + value);
        });

        const response: AxiosResponse<BusinessCard> = await api.post('/cards/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        console.log('Create response:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Create error:', error);
      throw error;
    }
  },

  async update(id: string, cardData: any): Promise<BusinessCard> {
    // FormDataかJSONかを判定
    if (cardData instanceof FormData) {
      const response: AxiosResponse<BusinessCard> = await api.put(`/cards/${id}`, cardData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // JSONデータをFormDataに変換
      const formData = new FormData();
      Object.entries(cardData).forEach(([key, value]) => {
        if (key === 'tag_names' && Array.isArray(value) && value.length > 0) {
          formData.append('tag_names', value.join(','));
        } else if (key === 'full_name' || key === 'full_name_reading') {
          // full_name と full_name_reading は空でも送信
          console.log(`🔥 Appending ${key}: '${value || ''}'`);
          formData.append(key, String(value || ''));
        } else if (value !== null && value !== undefined && value !== '' && key !== 'tag_names') {
          formData.append(key, String(value));
        }
      });

      console.log('FormData entries for update:');
      formData.forEach((value, key) => {
        console.log(key + ': ' + value);
      });

      const response: AxiosResponse<BusinessCard> = await api.put(`/cards/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Update response:', response.data);
      return response.data;
    }
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/cards/${id}`);
  },
};

export default api;